import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Sparkles, Briefcase, Brain, Target, Globe } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ROLES, SENIORITY, COMPANY_STAGES, INDUSTRIES, EXPERTISE, GOALS, OPEN_TO, TOOLS } from "@/lib/onboarding";
import { StarterPack } from "@/components/StarterPack";

function Chips({ options, value, onToggle, max, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button key={o} type="button" onClick={() => onToggle(o)} data-testid={`${testid}-${o}`}
            disabled={!active && max && value.length >= max}
            className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors disabled:opacity-40 ${
              active ? "bg-cobalt text-white border-cobalt" : "border-border text-foreground hover:border-cobalt hover:text-cobalt"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

function Single({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(value === o ? "" : o)} data-testid={`${testid}-${o}`}
          className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors ${
            value === o ? "bg-cobalt text-white border-cobalt" : "border-border text-foreground hover:border-cobalt hover:text-cobalt"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

const STEPS = [
  { id: "role", icon: Briefcase, eyebrow: "About you", title: "What best describes your work?" },
  { id: "expertise", icon: Brain, eyebrow: "Your strengths", title: "What are you great at?" },
  { id: "seeking", icon: Sparkles, eyebrow: "Your curiosity", title: "What do you want to learn or explore?" },
  { id: "goals", icon: Target, eyebrow: "Your intent", title: "Why are you here?" },
  { id: "context", icon: Globe, eyebrow: "Almost done", title: "A few finishing touches" },
];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showStarter, setShowStarter] = useState(false);
  const [f, setF] = useState({
    role: [], seniority: "", company_stage: "", industry: [],
    expertise: [], seeking: [], tools: [], goals: [], open_to: [],
    location: "", timezone: "", bio: "",
    links: { linkedin: "", github: "", website: "" },
  });

  useEffect(() => {
    // prefill from existing profile so this doubles as an editor
    if (!user) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const arr = (v) => Array.isArray(v) ? v : (v ? [v] : []);
    api.get(`/members/${user.user_id}`).then((r) => {
      const d = r.data;
      setF((prev) => ({
        ...prev,
        role: arr(d.role), seniority: d.seniority || "", company_stage: d.company_stage || "",
        industry: arr(d.industry), expertise: d.expertise || [], seeking: d.seeking || [],
        tools: d.tools || [], goals: d.goals || [], open_to: d.open_to || [],
        location: d.location || "", timezone: d.timezone || tz, bio: d.bio || "",
        links: { linkedin: d.links?.linkedin || "", github: d.links?.github || "", website: d.links?.website || "" },
      }));
    }).catch(() => setF((prev) => ({ ...prev, timezone: tz })));
  }, [user]);

  const toggle = (key, v) => setF((p) => ({ ...p, [key]: p[key].includes(v) ? p[key].filter((x) => x !== v) : [...p[key], v] }));
  const set = (key, v) => setF((p) => ({ ...p, [key]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.put("/onboarding", f);
      await refresh();
      toast.success("Welcome to Ikigora! · +25 pts");
      setShowStarter(true);
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setBusy(true);
    try { await api.put("/onboarding", f); await refresh(); navigate("/feed"); }
    catch (e) { navigate("/feed"); }
    finally { setBusy(false); }
  };

  const s = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-background dot-grid flex flex-col">
      <header className="flex items-center justify-between px-5 sm:px-10 h-16 border-b border-border bg-paper/80 backdrop-blur-md">
        <div className="flex items-center gap-2.5">
          <img src="/ikigora-icon.png" alt="Ikigora" className="w-8 h-8 object-contain" />
          <span className="font-serif text-lg font-extrabold tracking-tight">Ikigora</span>
        </div>
        <button onClick={skip} disabled={busy} data-testid="onboarding-skip"
          className="text-sm text-muted-foreground hover:text-foreground transition-colors">Skip for now</button>
      </header>

      {showStarter ? (
        <div className="flex-1 w-full max-w-2xl mx-auto px-5 py-8 sm:py-12">
          <StarterPack onDone={() => navigate("/feed")} />
        </div>
      ) : (
      <div className="flex-1 w-full max-w-2xl mx-auto px-5 py-8 sm:py-12">
        {/* progress */}
        <div className="flex items-center gap-1.5 mb-8" data-testid="onboarding-progress">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-cobalt" : "bg-border"}`} />
          ))}
        </div>

        <div className="fade-up" key={s.id}>
          <div className="inline-flex items-center gap-2 eyebrow text-cobalt mb-2"><s.icon size={14} /> {s.eyebrow}</div>
          <h1 className="font-serif font-extrabold text-3xl sm:text-4xl tracking-tight">{s.title}</h1>

          <div className="mt-8 space-y-8">
            {s.id === "role" && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">Your role <span className="text-muted-foreground font-normal">— pick one or more</span></label>
                  <p className="text-xs text-muted-foreground mb-3">Wear more than one hat? Select all that apply (up to 3).</p>
                  <Chips options={ROLES} value={f.role} onToggle={(v) => toggle("role", v)} max={3} testid="ob-role" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-3 block">Seniority</label>
                  <Single options={SENIORITY} value={f.seniority} onChange={(v) => set("seniority", v)} testid="ob-seniority" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-3 block">Company stage</label>
                  <Single options={COMPANY_STAGES} value={f.company_stage} onChange={(v) => set("company_stage", v)} testid="ob-stage" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Industry / domain <span className="text-muted-foreground font-normal">— pick one or more</span></label>
                  <p className="text-xs text-muted-foreground mb-3">Select up to 3.</p>
                  <Chips options={INDUSTRIES} value={f.industry} onToggle={(v) => toggle("industry", v)} max={3} testid="ob-industry" />
                </div>
              </>
            )}

            {s.id === "expertise" && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">Areas of expertise <span className="text-muted-foreground font-normal">— pick what you can help others with</span></label>
                  <p className="text-xs text-muted-foreground mb-3">Select up to 8.</p>
                  <Chips options={EXPERTISE} value={f.expertise} onToggle={(v) => toggle("expertise", v)} max={8} testid="ob-expertise" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-3 block">Tools & stack <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <Chips options={TOOLS} value={f.tools} onToggle={(v) => toggle("tools", v)} max={12} testid="ob-tools" />
                </div>
              </>
            )}

            {s.id === "seeking" && (
              <div>
                <label className="text-sm font-medium mb-1 block">What you want to learn or explore</label>
                <p className="text-xs text-muted-foreground mb-3">We'll match you with people strong in these areas. Select up to 8.</p>
                <Chips options={EXPERTISE} value={f.seeking} onToggle={(v) => toggle("seeking", v)} max={8} testid="ob-seeking" />
              </div>
            )}

            {s.id === "goals" && (
              <>
                <div>
                  <label className="text-sm font-medium mb-3 block">Your goals on Ikigora</label>
                  <Chips options={GOALS} value={f.goals} onToggle={(v) => toggle("goals", v)} max={5} testid="ob-goals" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-3 block">You're open to</label>
                  <Chips options={OPEN_TO} value={f.open_to} onToggle={(v) => toggle("open_to", v)} testid="ob-open-to" />
                </div>
              </>
            )}

            {s.id === "context" && (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Location</label>
                    <input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Berlin, Germany"
                      data-testid="ob-location" className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-cobalt transition-colors" />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Timezone</label>
                    <input value={f.timezone} onChange={(e) => set("timezone", e.target.value)} placeholder="Auto-detected"
                      data-testid="ob-timezone" className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-cobalt transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Short bio</label>
                  <textarea value={f.bio} onChange={(e) => set("bio", e.target.value)} rows={3} placeholder="A sentence or two about you…"
                    data-testid="ob-bio" className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-cobalt transition-colors resize-none" />
                </div>
                <div className="space-y-3">
                  {["linkedin", "github", "website"].map((k) => (
                    <div key={k}>
                      <label className="text-sm font-medium mb-2 block capitalize">{k}</label>
                      <input value={f.links[k]} onChange={(e) => set("links", { ...f.links, [k]: e.target.value })}
                        placeholder={k === "website" ? "https://…" : `Your ${k} URL`} data-testid={`ob-link-${k}`}
                        className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-cobalt transition-colors" />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="mt-10 flex items-center justify-between">
            <button onClick={() => setStep((v) => Math.max(0, v - 1))} disabled={step === 0} data-testid="onboarding-back"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-0">
              <ArrowLeft size={15} /> Back
            </button>
            {last ? (
              <button onClick={submit} disabled={busy} data-testid="onboarding-finish"
                className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-6 py-3 text-sm font-semibold hover:bg-gold-hover transition-colors disabled:opacity-50">
                <Check size={16} /> Finish & find my people
              </button>
            ) : (
              <button onClick={() => setStep((v) => v + 1)} data-testid="onboarding-next"
                className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-6 py-3 text-sm font-semibold hover:bg-gold-hover transition-colors">
                Continue <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
